import React, { useEffect, useState } from 'react';
import { Box, Snackbar, Alert, Slide, CircularProgress, keyframes, Tooltip, Chip } from '@mui/material';
import { useWebSocket } from './WebSocketContext';
import { useNavigate } from 'react-router-dom';
import '../styles/Sidebar.css';

// Animation keyframes for the floating effect
const floatAnimation = keyframes`
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-3px);
  }
  100% {
    transform: translateY(0px);
  }
`;



const shimmerAnimation = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

const StatusBar = () => {
  const { subscribeToMessages } = useWebSocket();
  const [filesCount, setFilesCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [serverMessages, setServerMessages] = useState([]);
  const primaryColor = '#5757b0';
  const lightPurple = '#f0f0ff';
  const navigate = useNavigate();
  const MAX_NOTIFICATIONS = 6;
  const MAX_SERVER_MESSAGES = 3;

  useEffect(() => {
    const unsubscribe = subscribeToMessages((parsedMessage) => {
      console.log('Notification message received:', parsedMessage);

      try {
        // Message is already parsed in WebSocketContext
        const message = parsedMessage;
        if (message.command === 'file_dequeued') {
          console.log('Pipeline status update:', message.data);
          // Use total_count (queued + processing) if available, otherwise fallback to legacy count
          const count = Number(message.data?.total_count ?? message.data?.count ?? 0);
          setFilesCount(Number.isFinite(count) ? count : 0);
        }

        if (message.command === "notifications") {
          setNotifications(prev => {
            // Create new notification
            const newNotification = {
              caseid: message.data.id,
              title: message.data.title,
              filename: message.data.filename
            };

            // Add to the beginning to keep newest at the top
            const updatedNotifications = [newNotification, ...prev];

            // Limit to MAX_NOTIFICATIONS by removing oldest (now at the end)
            if (updatedNotifications.length > MAX_NOTIFICATIONS) {
              return updatedNotifications.slice(0, MAX_NOTIFICATIONS);
            }

            return updatedNotifications;
          });
          console.log(message.data);
        }

        if (message.command === "notificationMessage") {
          setServerMessages(prev => {
            // Create new server message notification
            const newMessage = {
              id: Date.now(), // Use timestamp as unique ID
              message: message.data,
              type: 'info', // info, warning, error, success
              timestamp: new Date()
            };

            // Add to the beginning to keep newest at the top
            const updatedMessages = [newMessage, ...prev];

            // Limit to MAX_SERVER_MESSAGES by removing oldest
            if (updatedMessages.length > MAX_SERVER_MESSAGES) {
              return updatedMessages.slice(0, MAX_SERVER_MESSAGES);
            }

            return updatedMessages;
          });
          console.log('Server message:', message.data);
        }
      } catch (error) {
        console.error('Error processing WebSocket message:', error);
      }
    });
    return unsubscribe;
  }, [subscribeToMessages]); const handleCloseNotification = (index) => (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    // No need to stop propagation since the Alert no longer has onClick
    setNotifications(prev => prev.filter((_, i) => i !== index));
  };

  const handleCloseServerMessage = (messageId) => (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setServerMessages(prev => prev.filter(msg => msg.id !== messageId));
  };
  const handleNotificationClick = (caseId) => {
    navigate(`/case/${caseId}`);
  };

  // Helper function to get the appropriate severity and colors for server messages
  const getMessageSeverityAndColors = (type) => {
    switch (type) {
      case 'error':
        return {
          severity: 'error',
          bgColor: '#ffebee',
          borderColor: '#f44336',
          textColor: '#d32f2f'
        };
      case 'warning':
        return {
          severity: 'warning',
          bgColor: '#fff8e1',
          borderColor: '#ff9800',
          textColor: '#f57c00'
        };
      case 'success':
        return {
          severity: 'success',
          bgColor: '#e8f5e8',
          borderColor: '#4caf50',
          textColor: '#2e7d32'
        };
      default: // 'info'
        return {
          severity: 'info',
          bgColor: lightPurple,
          borderColor: primaryColor,
          textColor: primaryColor
        };
    }
  }; return (
    <>
      {filesCount > 0 && (
        <Tooltip
          title={`${filesCount} file${filesCount > 1 ? 's' : ''} currently being processed by the system. This includes files being analyzed, parsed, and indexed.`}
          placement="bottom-start"
          arrow
          sx={{
            '& .MuiTooltip-tooltip': {
              backgroundColor: 'rgba(233, 30, 99, 0.95)',
              color: 'white',
              fontSize: '0.75rem',
              borderRadius: '8px',
              maxWidth: '300px',
            },
            '& .MuiTooltip-arrow': {
              color: 'rgba(233, 30, 99, 0.95)',
            }
          }}
        >
          <Box sx={{
            position: 'fixed',
            top: 20,
            left: 20,
            zIndex: 1200,
            animation: `${floatAnimation} 4s ease-in-out infinite`,
            cursor: 'help',
          }}
          >
            <Chip
              icon={
                <CircularProgress
                  size={14}
                  sx={{
                    color: 'white',
                    '& .MuiCircularProgress-circle': {
                      strokeLinecap: 'round',
                    }
                  }}
                />
              }
              label={`${filesCount} in pipeline`}
              sx={{
                backgroundColor: primaryColor,
                color: 'white',
                fontWeight: 600,
                fontSize: '0.75rem',
                height: '32px',
                borderRadius: '16px', boxShadow: '0 4px 20px rgba(233, 30, 99, 0.3)',
                backdropFilter: 'blur(10px)',
                border: `2px solid ${lightPurple}`, '&:hover': {
                  background: 'linear-gradient(135deg,rgb(255, 205, 222) 0%, #5c6bc0 100%)',
                  boxShadow: '0 6px 25px rgba(233, 30, 99, 0.4)',
                  transform: 'translateY(-1px)',
                },
                '& .MuiChip-label': {
                  paddingLeft: '4px',
                  paddingRight: '12px',
                  color: 'white',
                  textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                },
                '& .MuiChip-icon': {
                  marginLeft: '8px',
                  marginRight: '0px',
                }, transition: 'all 0.3s ease-in-out',
                background: `linear-gradient(135deg,rgb(255, 203, 220) 0%, #3f51b5 100%)`,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '-200px',
                  width: '200px',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                  animation: `${shimmerAnimation} 3s infinite`,
                }
              }}
            />
          </Box>
        </Tooltip>
      )}{/* Notifications - limited to MAX_NOTIFICATIONS */}
      {notifications.map((notification, index) => (
        (notification.title && notification.filename) && (
          <Snackbar
            key={index}
            open={true}
            autoHideDuration={3000}
            onClose={handleCloseNotification(index)}
            TransitionComponent={Slide}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            style={{ marginBottom: index * 60 }}
          >
            <Alert
              onClose={handleCloseNotification(index)}
              severity="info"
              sx={{
                width: '100%',
                backgroundColor: lightPurple,
                color: primaryColor,
                border: `1px solid ${primaryColor}`,
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(87, 87, 176, 0.15)',
                '& .MuiAlert-icon': {
                  color: primaryColor
                }
              }}
            >              Project <span
              onClick={() => handleNotificationClick(notification.caseid)}
              style={{
                textDecoration: 'underline',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
                {notification.title}
              </span> has an update: {notification.filename}
            </Alert>
          </Snackbar>
        )
      ))}

      {/* Server Messages - limited to MAX_SERVER_MESSAGES */}
      {serverMessages.map((serverMessage, index) => {
        const { severity, bgColor, borderColor, textColor } = getMessageSeverityAndColors(serverMessage.type);
        return (
          <Snackbar
            key={serverMessage.id}
            open={true}
            autoHideDuration={serverMessage.type === 'error' ? 6000 : 4000} // Error messages stay longer
            onClose={handleCloseServerMessage(serverMessage.id)}
            TransitionComponent={Slide}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            style={{
              marginBottom: index * 60,
              zIndex: 1400 // Higher than notifications to appear on top
            }}
          >
            <Alert
              onClose={handleCloseServerMessage(serverMessage.id)}
              severity={severity}
              sx={{
                width: '100%',
                backgroundColor: bgColor,
                color: textColor,
                border: `1px solid ${borderColor}`,
                borderRadius: '12px',
                boxShadow: `0 4px 12px ${borderColor}25`,
                '& .MuiAlert-icon': {
                  color: textColor
                },
                '& .MuiAlert-message': {
                  fontWeight: 500
                }
              }}
            >
              {serverMessage.message}
            </Alert>
          </Snackbar>
        );
      })}
    </>
  );
};

export default StatusBar;
